import { ActionIcon, Button, SegmentedControl, Stack, Text, TextInput, Tooltip } from '@mantine/core'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { formatMoney } from '@tnn/core'
import { MoneyField } from '@tnn/ui'
import type { CustomFieldProps } from '../resource/config'
import { FileThumb, FileUpload } from './file'
import classes from './money.module.css'

/** One part of the money on a document: how it came in, how much, and for transfers or cheques the reference and a photo */
export interface MoneyLine {
  method: string
  amount: number
  reference?: string | null
  slipId?: string | null
}

export interface MoneyLinesFieldProps extends CustomFieldProps {
  /** The methods offered, in order */
  methods: readonly string[]
  /** What a line added with the button starts as; the first method by default */
  addMethod?: string
  methodLabel: (method: string) => string
  /** Labels: the amount, reference and slip inputs, and the add / remove buttons */
  text: (key: 'amount' | 'reference' | 'slip' | 'add' | 'remove') => string
}

/**
 * A document's money in parts (a receipt's payments, a customer payment's transfers and
 * cheques): a list of method + amount, with a reference and slip photo for anything but cash.
 * Read-only once the document is issued; editable on drafts and when editing after submit.
 */
export function MoneyLinesField({ form, readOnly, field, t, methods, addMethod, methodLabel, text }: MoneyLinesFieldProps) {
  const lines = (form.values[field.name] ?? []) as MoneyLine[]
  const error = form.errors[field.name]
  const set = (i: number, patch: Partial<MoneyLine>) => form.setFieldValue(`${field.name}.${i}`, { ...lines[i], ...patch })

  if (readOnly) {
    return (
      <div className={classes.lines}>
        {lines.map((line, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: money lines have no id
          <div key={i} className={classes.row}>
            <div className={classes.text}>
              <span>{methodLabel(line.method)}</span>
              {line.reference ? (
                <Text span size="xs" c="dimmed" truncate>
                  {line.reference}
                </Text>
              ) : null}
            </div>
            {line.slipId ? <FileThumb id={line.slipId} label={text('slip')} /> : null}
            <span className="tnn-num">{formatMoney(line.amount)}</span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <Stack gap="sm">
      {lines.map((line, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: money lines have no id
        <div key={i} className={classes.line}>
          <div className={classes.main}>
            <SegmentedControl
              size="sm"
              radius="xl"
              value={line.method}
              onChange={(method) => set(i, { method })}
              data={methods.map((m) => ({ value: m, label: methodLabel(m) }))}
            />
            <MoneyField
              aria-label={text('amount')}
              unit={t('units.baht')}
              value={line.amount}
              onChange={(v) => set(i, { amount: Number(v) || 0 })}
              className={classes.money}
            />
            {lines.length > 1 && (
              <Tooltip label={text('remove')}>
                <ActionIcon onClick={() => form.removeListItem(field.name, i)} aria-label={text('remove')}>
                  <IconTrash size={18} stroke={1.75} />
                </ActionIcon>
              </Tooltip>
            )}
          </div>
          {line.method !== 'cash' && (
            <div className={classes.pair}>
              <TextInput
                label={text('reference')}
                value={line.reference ?? ''}
                onChange={(e) => set(i, { reference: e.currentTarget.value })}
              />
              <FileUpload
                label={text('slip')}
                // A transfer is taken with its slip (`slipsMissing`); a cheque's photo may be left out
                withAsterisk={line.method === 'transfer'}
                error={form.errors[`${field.name}.${i}.slipId`]}
                value={line.slipId ?? null}
                onChange={(slipId) => set(i, { slipId })}
              />
            </div>
          )}
        </div>
      ))}
      <Button
        variant="subtle"
        leftSection={<IconPlus size={18} />}
        onClick={() => form.insertListItem(field.name, { method: addMethod ?? methods[0], amount: 0, reference: '', slipId: null })}
        style={{ alignSelf: 'flex-start' }}
      >
        {text('add')}
      </Button>
      {typeof error === 'string' && (
        <Text c="var(--tnn-error)" size="sm">
          {error}
        </Text>
      )}
    </Stack>
  )
}
